import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { BACKEND_URL } from "../config";
import "./WorkoutsHub.css";

/**
 * Updated WorkoutsHub
 * - Upcoming workouts always use a carousel-style experience
 * - Mobile uses a day picker above the carousel instead of side arrows
 * - Edit button opens popup modal for modifying the selected workout
 * - Generate section is replaced with a loading card while a plan is generating
 * - Top tab links removed
 */

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function useIsMobile(breakpoint = 809) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

function WorkoutDayCarousel({ items, renderItem, className = "" }) {
  const isMobile = useIsMobile();
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
  }, [items.length]);

  const goPrev = useCallback(() => {
    setActiveIndex((idx) => Math.max(0, idx - 1));
  }, []);

  const goNext = useCallback(() => {
    setActiveIndex((idx) => Math.min(Math.max(items.length - 1, 0), idx + 1));
  }, [items.length]);

  if (!items.length) return null;

  if (!isMobile) {
    return (
      <div className={`wh-workout-grid ${className}`.trim()}>
        {items.map((item, index) => (
          <div key={item.event_id || item.plan_id || index}>{renderItem(item, index)}</div>
        ))}
      </div>
    );
  }

  const activeItem = items[activeIndex];
  const activeDate = String(activeItem?.start_at || "").slice(0, 10);

  return (
    <div className={`wh-day-carousel ${className}`.trim()}>
      <div className="wh-day-picker">
        <button
          type="button"
          className="wh-day-picker__arrow"
          onClick={goPrev}
          aria-label="Previous workout day"
          disabled={activeIndex === 0}
        >
          ‹
        </button>

        <div className="wh-day-picker__center">
          <div className="body-small" style={{ color: "var(--text-muted)" }}>Workout day</div>
          <div className="wh-day-picker__label">
            {activeDate || `Workout ${activeIndex + 1}`}
          </div>
        </div>

        <button
          type="button"
          className="wh-day-picker__arrow"
          onClick={goNext}
          aria-label="Next workout day"
          disabled={activeIndex >= items.length - 1}
        >
          ›
        </button>
      </div>

      <div className="wh-day-carousel__card">
        {renderItem(activeItem, activeIndex)}
      </div>

      {items.length > 1 ? (
        <div className="wh-carousel-dots" role="tablist" aria-label="Workout pages">
          {items.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`wh-carousel-dot ${index === activeIndex ? "is-active" : ""}`}
              aria-label={`Go to workout ${index + 1}`}
              aria-selected={index === activeIndex}
              onClick={() => setActiveIndex(index)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function WorkoutsHub({ token, onStartWorkout, onBackToLogging, showToast }) {
  const [loading, setLoading] = useState(false);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);

  const [events, setEvents] = useState([]);
  const [plans, setPlans] = useState([]);

  const [genStartDate, setGenStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [genLengthDays, setGenLengthDays] = useState(30);
  const [genDaysPerWeek, setGenDaysPerWeek] = useState(3);
  const [genAiChooseDaysPerWeek, setGenAiChooseDaysPerWeek] = useState(true);
  const [genAutoRegen, setGenAutoRegen] = useState(false);
  const [genUseAI, setGenUseAI] = useState(true);

  const [selectedEventId, setSelectedEventId] = useState("");
  const [replaceIndex, setReplaceIndex] = useState(0);

  const [showAddPicker, setShowAddPicker] = useState(false);
  const [showReplacePicker, setShowReplacePicker] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [modSearch, setModSearch] = useState("");
  const [modLibrary, setModLibrary] = useState([]);
  const [addExerciseId, setAddExerciseId] = useState("");
  const [replaceFromIndex, setReplaceFromIndex] = useState(0);
  const [replaceExerciseId, setReplaceExerciseId] = useState("");
  const [useAiPick, setUseAiPick] = useState(true);

  const [q, setQ] = useState("");
  const [library, setLibrary] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);

  const [showManualWorkoutModal, setShowManualWorkoutModal] = useState(false);
  const [manualWorkoutPlanId, setManualWorkoutPlanId] = useState("");
  const [manualWorkout, setManualWorkout] = useState({
    name: "",
    day: 1,
    notes: "",
    exercises: [{ name: "", sets: 3, reps: 10, notes: "" }],
  });

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const attachedPlanIds = useMemo(
    () => new Set((plans || []).map((p) => getPlanId(p)).filter(Boolean)),
    [plans]
  );
  
  const isEventAttachedToVisiblePlan = useCallback(
    (ev) => {
      const eventPlanId = getEventPlanId(ev);
      if (!eventPlanId) return false;
      return attachedPlanIds.has(eventPlanId);
    },
    [attachedPlanIds]
  );


  const visibleEvents = useMemo(
    () => (events || []).filter(isEventAttachedToVisiblePlan),
    [events, isEventAttachedToVisiblePlan]
  );

  const selectedEvent = useMemo(
    () => visibleEvents.find((e) => e.event_id === selectedEventId) || null,
    [visibleEvents, selectedEventId]
  );

  const selectedSubItems = Array.isArray(selectedEvent?.sub_items) ? selectedEvent.sub_items : [];

  const getEventPlanId = (ev) =>
    ev?.plan_id ||
    ev?.workout_plan_id ||
    ev?.meta?.plan_id ||
    ev?.meta?.workout_plan_id ||
    ev?.meta?.source_plan_id ||
    ev?.meta?.program_id ||
    "";

  const getPlanId = (plan) =>
    plan?.plan_id ||
    plan?.workout_plan_id ||
    plan?.id ||
    "";

  

  
  

  const isWorkoutEvent = (ev) => {
    const kind = String(ev?.meta?.kind || ev?.category || "").toLowerCase();
    return kind === "workout" || kind === "workout_event" || kind === "workoutplan";
  };

  const dedupeEvents = (items) => {
    const byId = new Map();
    for (const ev of items || []) {
      if (!ev?.event_id) continue;
      if (!byId.has(ev.event_id)) byId.set(ev.event_id, ev);
    }
    return Array.from(byId.values()).sort((a, b) =>
      String(a?.start_at || "").localeCompare(String(b?.start_at || ""))
    );
  };

  const resetModifyUi = () => {
    setShowAddPicker(false);
    setShowReplacePicker(false);
    setAddExerciseId("");
    setReplaceExerciseId("");
    setModSearch("");
    setReplaceFromIndex(0);
    setReplaceIndex(0);
  };

  const closeReplaceModal = () => {
    setShowReplacePicker(false);
    setReplaceExerciseId("");
    setModSearch("");
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    closeReplaceModal();
    setShowAddPicker(false);
  };

  const closeManualWorkoutModal = () => {
    setShowManualWorkoutModal(false);
    setManualWorkoutPlanId("");
    setManualWorkout({
      name: "",
      day: 1,
      notes: "",
      exercises: [{ name: "", sets: 3, reps: 10, notes: "" }],
    });
  };

  const openEventEditor = (eventId) => {
    setSelectedEventId(eventId);
    resetModifyUi();
    setShowEditModal(true);
  };

  const loadEventsByScope = async (scope = "upcoming") => {
    if (!token) return;
    setLoading(true);
    try {
      const start = new Date();
      const end = new Date();
      if (scope === "all") {
        start.setDate(start.getDate() - 180);
        end.setDate(end.getDate() + 365);
      } else {
        end.setDate(end.getDate() + 14);
      }

      const qs = new URLSearchParams();
      qs.set("start", start.toISOString().slice(0, 10));
      qs.set("end", end.toISOString().slice(0, 10));

      const res = await fetch(`${BACKEND_URL}/api/calendar/events?${qs.toString()}`, {
        headers: authHeaders,
        cache: "no-store",
      });
      if (!res.ok) throw new Error(await res.text());

      const data = await res.json();
      const evs = dedupeEvents((data.events || []).filter(isWorkoutEvent));
      setEvents(evs);

      setSelectedEventId((prev) => {
        if (prev && evs.some((e) => e.event_id === prev)) return prev;
        return evs[0]?.event_id || "";
      });
    } catch (e) {
      console.warn("Load workouts failed:", e?.message || e);
      setEvents([]);
      setSelectedEventId("");
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/workouts/plans`, {
        headers: authHeaders,
        cache: "no-store",
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setPlans(Array.isArray(data?.plans) ? data.plans : []);
    } catch (e) {
      console.warn("Load workout plans failed:", e?.message || e);
      setPlans([]);
    }
  };

  const loadModLibrary = async (query = "") => {
    if (!token) return;
    try {
      const qs = new URLSearchParams();
      if (query) qs.set("q", query);
      const res = await fetch(`${BACKEND_URL}/api/workouts/library?${qs.toString()}`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setModLibrary(data.items || []);
    } catch (e) {
      console.warn("Modify library load failed:", e?.message || e);
      setModLibrary([]);
    }
  };

  const loadLibrary = async (query = "") => {
    if (!token) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (query) qs.set("q", query);
      const res = await fetch(`${BACKEND_URL}/api/workouts/library?${qs.toString()}`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setLibrary(data.items || []);
    } catch (e) {
      console.warn("Library load failed:", e?.message || e);
      setLibrary([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const d = Number(genLengthDays || 30);
    if (d > 30) setGenAutoRegen(true);
  }, [genLengthDays]);

  useEffect(() => {
    loadEventsByScope("upcoming");
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    loadLibrary(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  

  useEffect(() => {
    setSelectedEventId((prev) => {
      if (prev && visibleEvents.some((e) => e.event_id === prev)) return prev;
      return visibleEvents[0]?.event_id || "";
    });
  }, [visibleEvents]);

  const refreshProgramData = async () => {
    await Promise.all([loadEventsByScope("upcoming"), loadPlans()]);
  };

  const generatePlan = async () => {
    if (!token) return;
    setIsGeneratingPlan(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/workouts/ai/generate-plan`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          start_date: genStartDate,
          length_days: Math.max(7, Math.min(365, Number(genLengthDays) || 30)),
          days_per_week: Number(genDaysPerWeek),
          ai_choose_days_per_week: !!genAiChooseDaysPerWeek,
          auto_regen: !!genAutoRegen,
          ai: !!genUseAI,
          include_cardio: true,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      await res.json();
      await refreshProgramData();
      showToast?.("✅ Workout plan generated.", "success");
    } catch (e) {
      showToast?.("Generate failed: " + (e?.message || String(e)), "error");
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  const addExercise = async () => {
    if (!token || !selectedEventId) return;
    setLoading(true);
    try {
      const body = { action: "add_exercise", event_id: selectedEventId };
      if (!useAiPick && addExerciseId) body.exercise_id = addExerciseId;

      const res = await fetch(`${BACKEND_URL}/api/workouts/ai/modify`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());

      await loadEventsByScope("upcoming");
      showToast?.("✅ Added exercise.", "success");
      setShowAddPicker(false);
      setAddExerciseId("");
      setModSearch("");
    } catch (e) {
      showToast?.("Modify failed: " + (e?.message || String(e)), "error");
    } finally {
      setLoading(false);
    }
  };

  const replaceExercise = async () => {
    if (!token || !selectedEventId) return;
    setLoading(true);
    try {
      const idx = Number(replaceFromIndex);
      const body = { action: "replace_exercise", event_id: selectedEventId, index: idx };
      if (!useAiPick && replaceExerciseId) body.exercise_id = replaceExerciseId;

      const res = await fetch(`${BACKEND_URL}/api/workouts/ai/modify`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());

      await loadEventsByScope("upcoming");
      showToast?.("✅ Replaced exercise.", "success");
      closeReplaceModal();
    } catch (e) {
      showToast?.("Replace failed: " + (e?.message || String(e)), "error");
    } finally {
      setLoading(false);
    }
  };

  const removeExercise = async (idx) => {
    if (!token || !selectedEventId) return;
    if (!window.confirm("Delete this exercise from the workout?")) return;

    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/workouts/ai/modify`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "remove_exercise",
          event_id: selectedEventId,
          index: Number(idx),
        }),
      });
      if (!res.ok) throw new Error(await res.text());

      await loadEventsByScope("upcoming");
      showToast?.("✅ Exercise removed.", "success");
    } catch (e) {
      showToast?.("Delete exercise failed: " + (e?.message || String(e)), "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteAllWorkouts = async () => {
    if (!token) return;
    if (!window.confirm("Delete ALL workout events currently loaded in this view?")) return;

    const ids = Array.from(new Set((visibleEvents || []).map((e) => e?.event_id).filter(Boolean)));
    if (!ids.length) {
      showToast?.("No workouts to delete.", "info");
      return;
    }

    setLoading(true);
    try {
      const results = await Promise.allSettled(
        ids.map((eventId) =>
          fetch(`${BACKEND_URL}/api/calendar/events/${encodeURIComponent(eventId)}`, {
            method: "DELETE",
            headers: authHeaders,
          })
        )
      );

      const failed = results.filter((r) => r.status === "rejected" || (r.status === "fulfilled" && !r.value.ok));
      await refreshProgramData();

      if (failed.length > 0) {
        showToast?.(`Deleted ${ids.length - failed.length} workouts. ${failed.length} failed.`, "error");
      } else {
        showToast?.(`✅ Deleted ${ids.length} workouts.`, "success");
      }
    } catch (e) {
      showToast?.("Delete all failed: " + (e?.message || String(e)), "error");
    } finally {
      setSelectedEventId("");
      setLoading(false);
      closeEditModal();
    }
  };

  const deleteWorkoutEvent = async (eventId) => {
    if (!token || !eventId) return;
    if (!window.confirm("Delete this workout event?")) return;

    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/calendar/events/${encodeURIComponent(eventId)}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      if (!res.ok) throw new Error(await res.text());

      if (selectedEventId === eventId) setSelectedEventId("");
      await refreshProgramData();
      closeEditModal();
    } catch (e) {
      showToast?.("Delete failed: " + (e?.message || String(e)), "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteWorkoutPlan = async (planId) => {
    if (!token || !planId) return;
    if (!window.confirm("Delete this generated workout plan and all workouts in it?")) return;

    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/workouts/plans/${encodeURIComponent(planId)}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      if (!res.ok) throw new Error(await res.text());

      await refreshProgramData();
      showToast?.("✅ Workout plan deleted.", "success");
    } catch (e) {
      showToast?.("Delete plan failed: " + (e?.message || String(e)), "error");
    } finally {
      setLoading(false);
    }
  };

  const addManualExerciseRow = () => {
    setManualWorkout((prev) => ({
      ...prev,
      exercises: [...prev.exercises, { name: "", sets: 3, reps: 10, notes: "" }],
    }));
  };

  const updateManualExerciseRow = (idx, patch) => {
    setManualWorkout((prev) => ({
      ...prev,
      exercises: prev.exercises.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    }));
  };

  const removeManualExerciseRow = (idx) => {
    setManualWorkout((prev) => ({
      ...prev,
      exercises: prev.exercises.filter((_, i) => i !== idx),
    }));
  };

  const createManualWorkout = async () => {
    if (!token) return;
    const targetPlanId = manualWorkoutPlanId || plans[0]?.plan_id || "";
    if (!targetPlanId) {
      showToast?.("Generate a workout plan first so the manual workout can be attached to it.", "info");
      return;
    }
    if (!manualWorkout.name.trim()) {
      showToast?.("Workout name is required.", "info");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/workout-plans/${encodeURIComponent(targetPlanId)}/workouts`,
        {
          method: "POST",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({
            name: manualWorkout.name.trim(),
            day: Number(manualWorkout.day) || 1,
            notes: manualWorkout.notes || "",
            exercises: manualWorkout.exercises
              .filter((x) => x.name && x.name.trim())
              .map((x) => ({
                name: x.name.trim(),
                sets: Number(x.sets) || 0,
                reps: Number(x.reps) || 0,
                notes: x.notes || "",
              })),
          }),
        }
      );

      if (!res.ok) throw new Error(await res.text());

      closeManualWorkoutModal();
      await refreshProgramData();
      showToast?.("✅ Manual workout added.", "success");
    } catch (e) {
      showToast?.("Manual add failed: " + (e?.message || String(e)), "error");
    } finally {
      setLoading(false);
    }
  };

  const openReplaceModal = async (idx) => {
    setReplaceFromIndex(Number(idx));
    setReplaceIndex(Number(idx));
    setReplaceExerciseId("");
    setShowReplacePicker(true);
    if (!useAiPick && !modLibrary.length) {
      await loadModLibrary("");
    }
  };

  const renderWorkoutTile = (ev) => (
    <div className="wh-workout-tile">
      <div className="wh-workout-tile__meta">
        <div className="wh-workout-tile__title">{ev.title}</div>
        <div className="wh-workout-tile__sub">
          {String(ev.start_at || "").slice(0, 16).replace("T", " ")} •{" "}
          {Array.isArray(ev.sub_items) ? ev.sub_items.length : 0} exercises
        </div>
      </div>

      <div className="wh-workout-tile__actions">
        {onStartWorkout ? (
          <button className="btn-primary" type="button" onClick={(e) => { e.stopPropagation(); onStartWorkout(ev.event_id); }}>
            Start
          </button>
        ) : null}
        <button
          className="btn-secondary"
          type="button"
          onClick={(e) => { e.stopPropagation(); openEventEditor(ev.event_id); }}
        >
          Edit
        </button>
      </div>
    </div>
  );

  return (
    <div className="container workouts-hub" style={{ maxWidth: 1100 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div className="heading-2" style={{ margin: 0 }}>Workouts</div>
          <div className="body-small" style={{ color: "var(--text-muted)" }}>
            Upcoming workouts stay in a simple carousel, with a day picker on mobile and popup editing.
          </div>
        </div>

        <div className="wh-inline-actions">
          <button className="btn-secondary" type="button" onClick={refreshProgramData} disabled={loading || isGeneratingPlan}>
            Refresh
          </button>
        </div>
      </div>
      {plans.length > 0 && (
            <div style={{ marginTop: 16, marginBottom: 10 }}>
              <div className="body-small" style={{ color: "var(--text-muted)", marginBottom: 8 }}>
                Generated workout plans
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {plans.map((plan) => (
                  <div
                    key={plan.plan_id}
                    className="wh-plan-card"
                  >
                    <div>
                      <div style={{ fontWeight: 900 }}>Generated Workout Plans</div>
                      <div className="body-small" style={{ color: "var(--text-muted)" }}>
                        {plan.events || 0} workouts • {String(plan.start_at_min || "").slice(0, 10)} to{" "}
                        {String(plan.start_at_max || "").slice(0, 10)}
                      </div>
                    </div>

                    <button
                      className="btn-danger"
                      type="button"
                      onClick={() => deleteWorkoutPlan(plan.plan_id)}
                      disabled={loading || isGeneratingPlan}
                    >
                      Delete workout plan
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

      {(loading && !isGeneratingPlan) && (
        <div className="card" style={{ padding: 14, marginTop: 14 }}>
          <div className="body-medium">Loading…</div>
        </div>
      )}

      <div className="wh-page-stack">
        <section className="card wh-section-card" style={{ padding: 16, marginTop: 14 }}>
          {isGeneratingPlan ? (
            <div className="wh-generate-loading">
              <div className="heading-4" style={{ marginBottom: 8 }}>Generating workout plan</div>
              <div className="body-small" style={{ color: "var(--text-muted)" }}>
                Building your workout schedule and exercise selection now…
              </div>
            </div>
          ) : (
            <>
              <div className="heading-4" style={{ marginBottom: 10 }}>Generate a workout plan</div>

              <div className="form-row wh-two-col">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>Start date</label>
                  <input
                    className="form-input"
                    type="date"
                    value={genStartDate}
                    onChange={(e) => setGenStartDate(e.target.value)}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>Program length (days)</label>
                  <input
                    className="form-input"
                    type="number"
                    min="7"
                    max="365"
                    value={genLengthDays}
                    onChange={(e) => setGenLengthDays(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-row wh-two-col" style={{ marginTop: 12 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>Days per week</label>
                  <input
                    className="form-input"
                    type="number"
                    min="1"
                    max="6"
                    value={genDaysPerWeek}
                    onChange={(e) => setGenDaysPerWeek(e.target.value)}
                    disabled={genAiChooseDaysPerWeek}
                  />
                  <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 6 }}>
                    When “AI choose days/week” is enabled, this is used as a hint only.
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>Options</label>
                  <label className="body-small" style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={genAiChooseDaysPerWeek}
                      onChange={(e) => setGenAiChooseDaysPerWeek(e.target.checked)}
                    />
                    AI choose days/week
                  </label>
                  <label className="body-small" style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={genAutoRegen}
                      onChange={(e) => setGenAutoRegen(e.target.checked)}
                    />
                    Auto regenerate (keep plan fresh)
                  </label>
                </div>
              </div>

              <label className="body-small" style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12 }}>
                <input type="checkbox" checked={genUseAI} onChange={(e) => setGenUseAI(e.target.checked)} />
                Use AI for exercise selection (includes cardio when helpful)
              </label>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
                <button className="btn-primary" type="button" onClick={generatePlan} disabled={loading || isGeneratingPlan}>
                  Generate
                </button>
              </div>
            </>
          )}
        </section>

        <section className="card wh-section-card" style={{ padding: 16, marginTop: 14 }}>
          <div className="wh-section-head">
            <div className="wh-section-copy">
              <div className="heading-4" style={{ marginBottom: 0 }}>Upcoming workouts</div>
              <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 6 }}>
                Only workouts attached to an existing workout plan show here. Tap Edit to open the workout popup.
              </div>
            </div>

            <div className="wh-inline-actions">
              <button
                className="btn-secondary"
                type="button"
                onClick={() => {
                  if (!manualWorkoutPlanId) {
                    const currentPlanId = plans[0]?.plan_id || "";
                    if (currentPlanId) setManualWorkoutPlanId(currentPlanId);
                  }
                  setShowManualWorkoutModal(true);
                }}
                disabled={plans.length === 0 || loading || isGeneratingPlan}
              >
                Manual Add Workout
              </button>
            </div>
          </div>

          <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 6 }}>
            {plans.length || 0} plans • {visibleEvents.length || 0} workout events
          </div>

          

          {visibleEvents.length === 0 ? (
            <div className="body-medium" style={{ color: "var(--text-secondary)", marginTop: 12 }}>
              No workout events found. Generate a plan to add workouts to your calendar.
            </div>
          ) : (
            <div style={{ marginTop: 14 }}>
              <WorkoutDayCarousel items={visibleEvents} renderItem={renderWorkoutTile} />
            </div>
          )}
        </section>

        <section className="card wh-section-card" style={{ padding: 16, marginTop: 14 }}>
          <div className="heading-4" style={{ marginBottom: 10 }}>Form guidance (video bank)</div>

          <div className="form-row wh-search-row">
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="body-small" style={{ color: "var(--text-secondary)" }}>Search</label>
              <input className="form-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="bench, squat, row…" />
            </div>
            <button className="btn-secondary" type="button" onClick={() => loadLibrary(q)} style={{ height: 48 }}>
              Search
            </button>
          </div>

          <div className="wh-guidance-grid" style={{ marginTop: 14 }}>
            <div className="wh-guidance-list">
              <div className="wh-guidance-list__head">Exercises</div>
              <div className="wh-guidance-list__body">
                {(library || []).slice(0, 200).map((it) => (
                  <button
                    key={it.exercise_id}
                    type="button"
                    onClick={() => setSelectedExercise(it)}
                    className="wh-guidance-row"
                    style={{
                      background: selectedExercise?.exercise_id === it.exercise_id ? "rgba(0,128,255,0.10)" : "transparent",
                    }}
                  >
                    <div style={{ fontWeight: 800 }}>{it.name}</div>
                    <div className="body-small" style={{ color: "var(--text-muted)" }}>
                      {it.section || it.top_group || ""}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="wh-guidance-detail">
              {selectedExercise ? (
                <>
                  <div style={{ fontWeight: 900, fontSize: 16 }}>{selectedExercise.name}</div>
                  <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 6 }}>
                    {selectedExercise.section || selectedExercise.top_group || ""}
                    {selectedExercise.focus ? ` • ${selectedExercise.focus}` : ""}
                  </div>

                  {selectedExercise.youtube_url ? (
                    <a
                      href={selectedExercise.youtube_url}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-primary"
                      style={{ display: "inline-flex", marginTop: 12, textDecoration: "none" }}
                    >
                      ▶ Watch video
                    </a>
                  ) : (
                    <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 12 }}>No video link</div>
                  )}

                  {selectedExercise.notes ? (
                    <div className="wh-cues-box">
                      <div className="body-small" style={{ fontWeight: 800, color: "var(--text-muted)", marginBottom: 6 }}>Cues</div>
                      <div className="body-small" style={{ whiteSpace: "pre-wrap", color: "var(--text-primary)" }}>
                        {selectedExercise.notes}
                      </div>
                    </div>
                  ) : null}

                  {selectedExercise.raw ? (
                    <details style={{ marginTop: 12 }}>
                      <summary className="body-small" style={{ cursor: "pointer", color: "var(--brand-primary)", fontWeight: 800 }}>
                        View full details
                      </summary>
                      <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                        {Object.entries(selectedExercise.raw).map(([k, v]) => (
                          <div key={k} className="wh-raw-box">
                            <div className="body-small" style={{ color: "var(--text-muted)", fontWeight: 800 }}>{k}</div>
                            <div className="body-small" style={{ color: "var(--text-secondary)", whiteSpace: "pre-wrap" }}>
                              {String(v ?? "")}
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  ) : null}
                </>
              ) : (
                <div className="body-medium" style={{ color: "var(--text-secondary)" }}>
                  Select an exercise to view details.
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {showManualWorkoutModal && (
        <div className="modal-overlay" onMouseDown={closeManualWorkoutModal}>
          <div
            className="modal-content ct-form-modal"
            onMouseDown={(e) => e.stopPropagation()}
            style={{ width: "min(860px, 100%)", maxWidth: 860 }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
              <h3 className="heading-3" style={{ margin: 0 }}>Manual Add Workout</h3>
              <button type="button" className="wh-modal-close" aria-label="Close" onClick={closeManualWorkoutModal}>✕</button>
            </div>

            <div className="body-small" style={{ color: "var(--text-muted)", marginBottom: 8 }}>
              Manual workouts are attached to the selected current workout program.
            </div>

            <div className="form-group">
              <label className="body-small" style={{ color: "var(--text-secondary)" }}>Attach to workout plan</label>
              <select
                className="form-input"
                value={manualWorkoutPlanId}
                onChange={(e) => setManualWorkoutPlanId(e.target.value)}
              >
                <option value="">Select a plan…</option>
                {plans.map((p) => (
                  <option key={p.plan_id} value={p.plan_id}>
                    {String(p.start_at_min || "").slice(0, 10)} → {String(p.start_at_max || "").slice(0, 10)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row wh-manual-top-row">
              <div className="form-group">
                <label className="body-small" style={{ color: "var(--text-secondary)" }}>Workout name</label>
                <input
                  className="form-input"
                  value={manualWorkout.name}
                  onChange={(e) => setManualWorkout((p) => ({ ...p, name: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label className="body-small" style={{ color: "var(--text-secondary)" }}>Day</label>
                <input
                  className="form-input"
                  type="number"
                  min="1"
                  value={manualWorkout.day}
                  onChange={(e) => setManualWorkout((p) => ({ ...p, day: e.target.value }))}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="body-small" style={{ color: "var(--text-secondary)" }}>Notes</label>
              <textarea
                className="form-input"
                rows={2}
                value={manualWorkout.notes}
                onChange={(e) => setManualWorkout((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>

            <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
              {manualWorkout.exercises.map((row, idx) => (
                <div
                  key={idx}
                  className="wh-manual-row-card"
                >
                  <div className="form-row wh-manual-exercise-row">
                    <input
                      className="form-input"
                      placeholder="Exercise name"
                      value={row.name}
                      onChange={(e) => updateManualExerciseRow(idx, { name: e.target.value })}
                    />
                    <input
                      className="form-input"
                      type="number"
                      placeholder="Sets"
                      value={row.sets}
                      onChange={(e) => updateManualExerciseRow(idx, { sets: e.target.value })}
                    />
                    <input
                      className="form-input"
                      type="number"
                      placeholder="Reps"
                      value={row.reps}
                      onChange={(e) => updateManualExerciseRow(idx, { reps: e.target.value })}
                    />
                    <button type="button" className="btn-secondary" onClick={() => removeManualExerciseRow(idx)}>
                      Remove
                    </button>
                  </div>

                  <input
                    className="form-input"
                    placeholder="Notes"
                    value={row.notes}
                    onChange={(e) => updateManualExerciseRow(idx, { notes: e.target.value })}
                  />
                </div>
              ))}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
              <button type="button" className="btn-secondary" onClick={addManualExerciseRow}>
                + Add Exercise Row
              </button>

              <div style={{ display: "flex", gap: 10 }}>
                <button type="button" className="btn-secondary" onClick={closeManualWorkoutModal}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" onClick={createManualWorkout} disabled={loading || isGeneratingPlan}>
                  Save Workout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && selectedEvent && (
        <div className="modal-overlay" onMouseDown={closeEditModal}>
          <div
            className="modal-content ct-form-modal wh-edit-modal"
            onMouseDown={(e) => e.stopPropagation()}
            style={{ width: "min(960px, 100%)", maxWidth: 960 }}
          >
            <div className="wh-edit-modal__header">
              <div>
                <h3 className="heading-3" style={{ margin: 0 }}>{selectedEvent.title}</h3>
                <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 6 }}>
                  {String(selectedEvent.start_at || "").slice(0, 16).replace("T", " ")} • {selectedSubItems.length} exercises
                </div>
              </div>
              <button type="button" className="wh-modal-close" aria-label="Close" onClick={closeEditModal}>✕</button>
            </div>

            <div className="wh-edit-modal__body">
            <div className="wh-edit-modal__topbar">
              <label className="body-small wh-modify-event__toggle">
                <input type="checkbox" checked={useAiPick} onChange={(e) => setUseAiPick(e.target.checked)} disabled={loading} />
                Use AI to pick exercises
              </label>

              <div className="wh-inline-actions">
                <button
                  className="btn-secondary"
                  type="button"
                  onClick={() => {
                    setShowAddPicker((v) => !v);
                    setShowReplacePicker(false);
                    if (!modLibrary.length) loadModLibrary("");
                  }}
                  disabled={!selectedEventId || loading}
                >
                  Add exercise…
                </button>

                <button
                  className="btn-danger"
                  type="button"
                  onClick={() =>
                    modScope === "all"
                      ? deleteAllWorkouts()
                      : deleteWorkoutEvent(selectedEventId)
                  }
                  disabled={
                    modScope === "all"
                      ? visibleEvents.length === 0 || loading
                      : !selectedEventId || loading
                  }
                >
                  {modScope === "all" ? "Delete all workouts" : "Delete workout"}
                </button>
              </div>
            </div>

            {showAddPicker && selectedEventId && (
              <div className="wh-picker-panel">
                <div style={{ fontWeight: 900, marginBottom: 8 }}>Add an exercise</div>

                {!useAiPick && (
                  <>
                    <div className="form-row wh-search-row">
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="body-small" style={{ color: "var(--text-secondary)" }}>Search exercise library</label>
                        <input className="form-input" value={modSearch} onChange={(e) => setModSearch(e.target.value)} placeholder="bench, squat, row…" />
                      </div>
                      <button className="btn-secondary" type="button" onClick={() => loadModLibrary(modSearch)} style={{ height: 48 }} disabled={loading}>
                        Search
                      </button>
                    </div>

                    <div className="form-group" style={{ marginTop: 10 }}>
                      <label className="body-small" style={{ color: "var(--text-secondary)" }}>Choose exercise</label>
                      <select className="form-input" value={addExerciseId} onChange={(e) => setAddExerciseId(e.target.value)}>
                        <option value="">Select…</option>
                        {(modLibrary || []).slice(0, 250).map((it) => (
                          <option key={it.exercise_id} value={it.exercise_id}>
                            {it.name}{it.section ? ` — ${it.section}` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
                  <button className="btn-secondary" type="button" onClick={() => setShowAddPicker(false)} disabled={loading}>
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    type="button"
                    onClick={addExercise}
                    disabled={loading || !selectedEventId || (!useAiPick && !addExerciseId)}
                  >
                    Add
                  </button>
                </div>
              </div>
            )}

            <div className="wh-modify-event__list wh-edit-modal__list">
              {selectedSubItems.length === 0 ? (
                <div className="body-small" style={{ color: "var(--text-muted)" }}>No exercises found on this event.</div>
              ) : (
                selectedSubItems.map((si, idx) => (
                  <div
                    key={si.item_id || `${idx}-${si.name}`}
                    className="wh-modify-item"
                  >
                    <div className="wh-modify-item__info">
                      <div className="wh-modify-item__title">
                        {idx}. {si.name || "Exercise"}
                      </div>
                      <div className="body-small wh-modify-item__sub">
                        {si.sets ? `${si.sets} sets` : ""}{si.reps ? ` • ${si.reps} reps` : ""}{si.notes ? ` • ${si.notes}` : ""}
                      </div>
                    </div>

                    <div className="wh-modify-item__actions">
                      <button
                        className="btn-secondary"
                        type="button"
                        onClick={() => openReplaceModal(idx)}
                        disabled={loading}
                      >
                        Replace…
                      </button>

                      <button
                        className="btn-danger"
                        type="button"
                        onClick={() => removeExercise(idx)}
                        disabled={loading}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            </div>
          </div>
        </div>
      )}

      {showReplacePicker && selectedEventId && selectedSubItems.length > 0 && (
        <div className="modal-overlay" onMouseDown={closeReplaceModal}>
          <div
            className="modal-content ct-form-modal"
            onMouseDown={(e) => e.stopPropagation()}
            style={{ width: "min(760px, 100%)", maxWidth: 760 }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
              <h3 className="heading-3" style={{ margin: 0 }}>Replace Exercise</h3>
              <button type="button" className="wh-modal-close" aria-label="Close" onClick={closeReplaceModal}>✕</button>
            </div>

            <div className="form-row wh-two-col">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="body-small" style={{ color: "var(--text-secondary)" }}>Replace this</label>
                <select
                  className="form-input"
                  value={replaceFromIndex}
                  onChange={(e) => {
                    setReplaceFromIndex(Number(e.target.value));
                    setReplaceIndex(Number(e.target.value));
                  }}
                >
                  {selectedSubItems.map((si, idx) => (
                    <option key={si.item_id || idx} value={idx}>
                      {idx}. {si.name || "Exercise"}
                    </option>
                  ))}
                </select>
              </div>

              {!useAiPick ? (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>With this</label>
                  <select className="form-input" value={replaceExerciseId} onChange={(e) => setReplaceExerciseId(e.target.value)}>
                    <option value="">Select…</option>
                    {(modLibrary || []).slice(0, 250).map((it) => (
                      <option key={it.exercise_id} value={it.exercise_id}>
                        {it.name}{it.section ? ` — ${it.section}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="body-small" style={{ color: "var(--text-muted)", display: "flex", alignItems: "end" }}>
                  AI will pick a replacement from your library.
                </div>
              )}
            </div>

            {!useAiPick && (
              <div className="form-row wh-search-row" style={{ marginTop: 10 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>Search library</label>
                  <input className="form-input" value={modSearch} onChange={(e) => setModSearch(e.target.value)} placeholder="bench, squat, row…" />
                </div>
                <button className="btn-secondary" type="button" onClick={() => loadModLibrary(modSearch)} style={{ height: 48 }} disabled={loading}>
                  Search
                </button>
              </div>
            )}

            <div className="wh-picker-panel">
              <div style={{ fontWeight: 900, marginBottom: 6 }}>Current exercise</div>
              <div className="body-small" style={{ color: "var(--text-secondary)" }}>
                {selectedSubItems[replaceFromIndex]?.name || "Exercise"}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
              <button
                className="btn-danger"
                type="button"
                onClick={() => removeExercise(replaceFromIndex)}
                disabled={loading}
              >
                Delete Instead
              </button>

              <div style={{ display: "flex", gap: 10 }}>
                <button className="btn-secondary" type="button" onClick={closeReplaceModal} disabled={loading}>
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  type="button"
                  onClick={replaceExercise}
                  disabled={loading || !selectedEventId || selectedSubItems.length === 0 || (!useAiPick && !replaceExerciseId)}
                >
                  Replace
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
